// One login in detail, in a right-side drawer (row click or Details; deep link #login-<id>):
// lifecycle, jobs (with Skip this week on catch-up jobs), the sign-in budget, and the runs with
// their triage and Promote. The header carries the actions: Run now…, Schedule…, Stop retries
// today…, Turn off… / Turn on…, and Release… while it is in fix on dev.
//
// Each section reads only what /logins already sent, plus the history and screenshots it asks
// for on open; a 404 from an older API leaves today's plan entries and no inline screenshots.

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getHistory, getScreenshots } from './api';
import BudgetMeter from './BudgetMeter';
import {
    WARN, daysText, deskLabel, driftNotice, fmtDay, groupShots, historyRuns, isOffice, jobRows, lifecycleOf, loginDesk,
    needsEnroll, prodName, scheduleOf,
} from './deskModel';
import { fmtWhen } from './jobDays';
import RunTimeline from './RunTimeline';
import { Dot, Tag, TEST, useTones } from './tones';

const H = ({ children }) => (
    <Typography className="d-font" component="h3" sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
        {children}
    </Typography>
);
const Section = ({ children, ...rest }) => (
    <Box component="section" sx={{ px: 2.5, py: 2, borderTop: '1px solid var(--color-border, #e0e0e0)' }} {...rest}>{children}</Box>
);

const Stepper = ({ life }) => {
    const t = useTones();
    return (
        <Box sx={{ mt: 1.5 }}>
            <Box aria-label="Lifecycle" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {life.steps.map((s) => (
                    <Box key={s.label} component="span" className="d-font" data-step={s.state} sx={{
                        fontSize: 11.5, px: 1, py: '2px', borderRadius: 999, whiteSpace: 'nowrap',
                        border: '1px solid', borderColor: s.state === 'now' ? t.text('#1565c0') : 'var(--color-border, #e0e0e0)',
                        bgcolor: s.state === 'done' ? (t.dark ? 'rgba(129,199,132,0.16)' : '#e6f4ea') : 'transparent',
                        color: s.state === 'todo' ? t.muted : 'text.primary', fontWeight: s.state === 'now' ? 700 : 400,
                    }}>
                        {s.state === 'done' ? '✓ ' : ''}{s.label}
                    </Box>
                ))}
            </Box>
            {life.dots.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }} aria-label="Probation run days">
                    {life.dots.map((d) => (
                        <Tooltip key={d.day} title={`${d.day}: ${d.note}`}>
                            <Box component="span" className="d-font" sx={{ fontSize: 11.5, color: d.clean ? t.ok : t.bad }}>
                                {fmtDay(d.day)} {d.clean ? '✓' : '✗'}
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            )}
            {life.noRecord && (
                <Typography className="d-font" sx={{ fontSize: 11.5, color: t.muted, mt: 0.75 }}>
                    No probation record: it went live before the desk counted probation days.
                </Typography>
            )}
        </Box>
    );
};

const Jobs = ({ row, runs, canSkip, onSkip }) => {
    const t = useTones();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {jobRows(row, runs).map((j) => (
                <Box key={j.job_type} data-job={j.job_type}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography className="d-font" sx={{ fontSize: 13.5, fontWeight: 600 }}>{j.name}</Typography>
                        {j.completedToday ? <Tag label="Done today" colour="#2e7d32" />
                            : j.skippedThisWeek ? <Tag label={`Skipped this week${j.skippedThisWeek.by ? ` · by ${j.skippedThisWeek.by}` : ''}`} title={j.skippedThisWeek.reason || ''} />
                                : j.doneThisWeek ? <Tag label="Done this week" />
                                : j.dueToday ? <Tag label="Due today" outlined colour="#1565c0" /> : <Tag label="Not due" outlined />}
                        {j.override && <Tag label="override" colour={WARN} title={`Console override; the file says ${daysText((row.jobs.find((x) => x.job_type === j.job_type)?.days) || scheduleOf(row).days) || 'the login\'s days'}`} />}
                    </Box>
                    <Typography className="d-font" sx={{ fontSize: 12, color: t.muted, mt: 0.25 }}>
                        {j.daysText}{j.catchUp === 'week' ? ', catch-up later in the week' : ''}
                        {' · last live: '}{j.last ? `${j.last.status} ${fmtWhen(j.last.at, row.timezone, j.last.day, row.today?.day)}` : row.jobs_last ? 'none in 14 days' : '—'}
                        {j.lastTest && <> · last test <Tag label={`${j.lastTest.status} ${fmtWhen(j.lastTest.at, row.timezone, j.lastTest.day, row.today?.day)}`} colour={TEST} /></>}
                        {' · '}<code>{j.job_type}</code>
                    </Typography>
                    {j.catchUp === 'week' && canSkip && !j.doneThisWeek && !j.completedToday && (
                        <Button size="small" sx={{ textTransform: 'none', px: 0 }} onClick={() => onSkip(j.job_type)}>Skip this week…</Button>
                    )}
                </Box>
            ))}
        </Box>
    );
};

// act(kind, extra): opens the matching dialog in the console (run, schedule, stop, resume, off,
// on, fix, release, skip, promote, shot, shots, audit, enroll).
const LoginDrawer = ({ login, data, desks, onClose, act, refreshKey }) => {
    const t = useTones();
    const phone = useMediaQuery('(max-width:600px)');
    const [history, setHistory] = useState(null);
    const [shots, setShots] = useState({});
    const id = login?.id;
    // The history route ships with the per-login fields below; an older API has neither, so it isn't
    // asked (a 404 would only add a console error). Its runs then come from today's plan.
    const hasHistory = Boolean(login && 'stop_today' in login);
    useEffect(() => {
        setHistory(null); setShots({});
        if (!id) return undefined;
        let live = true;
        if (hasHistory) getHistory(id, 14).then((h) => { if (live) setHistory(h); }).catch(() => { /* today's runs */ });
        getScreenshots(id).then((d) => {
            if (!live) return;
            const byRun = {};
            groupShots(d).forEach((g) => { if (g.run_id) byRun[g.run_id] = g.files; });
            setShots(byRun);
        }).catch(() => { /* no screenshots inline */ });
        return () => { live = false; };
    }, [id, refreshKey, hasHistory]);

    const row = login;
    const runs = row ? historyRuns(history, row) : [];
    const office = row ? isOffice(row, data) : false;
    const life = row ? lifecycleOf(row) : null;
    const drift = row ? driftNotice(row) : null;
    const deskBusy = row ? Object.values(desks || {}).some((d) => d && !d.stale && d.credential === row.id) : false;
    // Newer API routes: stop_today / override_jobs are sent by the same release that adds them.
    const newer = row ? 'stop_today' in row : false;
    const client = row ? [...new Set((row.jobs || []).map((j) => j.client).filter(Boolean))].join(', ') : '';
    const c = row ? t.desk(loginDesk(row)) : null;

    return (
        <Drawer anchor="right" open={Boolean(row)} onClose={onClose}
            PaperProps={{
                role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'desk-drawer-title',
                sx: { width: phone ? '100%' : 560, maxWidth: '100%', bgcolor: 'background.paper' },
            }}>
            {row && (
                <Box data-drawer={row.id} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
                    <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography id="desk-drawer-title" component="h2" className="d-font" sx={{ fontSize: 17, fontWeight: 700, overflowWrap: 'anywhere' }}>{row.id}</Typography>
                                <Typography className="d-font" sx={{ fontSize: 12, color: t.muted, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    {client} · {row.vendor} · {row.login === 'sso' ? 'SSO' : 'form login'} · <Dot colour={c.dot} size={8} />
                                    {deskLabel(prodName(loginDesk(row)))}{office ? ' (office Mac)' : ''}
                                </Typography>
                            </Box>
                            <IconButton aria-label="Close" onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                            {needsEnroll(row) ? (
                                <Button size="small" variant="contained" color="warning" sx={{ textTransform: 'none' }} onClick={() => act('enroll')}>Enroll now</Button>
                            ) : (
                                <Button size="small" variant="contained" sx={{ textTransform: 'none' }}
                                    disabled={Boolean(row.disabled) || Boolean(row.fixing) || (!row.enabled && !office)} onClick={() => act('run')}>
                                    Run now…
                                </Button>
                            )}
                            <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => act('schedule')}>Schedule…</Button>
                            {newer && (row.stop_today
                                ? <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => act('resume')}>Allow retries today</Button>
                                : <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} disabled={Boolean(row.disabled) || row.today?.done}
                                    onClick={() => act('stop')}>Stop retries today…</Button>)}
                            {newer && row.fixing && (
                                <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => act('release')}>Release…</Button>
                            )}
                            {row.disabled
                                ? <Button size="small" variant="outlined" color="success" sx={{ textTransform: 'none' }} onClick={() => act('on')}>Turn on…</Button>
                                : <Button size="small" variant="outlined" color="error" sx={{ textTransform: 'none' }} onClick={() => act('off')}>Turn off…</Button>}
                        </Box>
                        {row.disabled && <Alert severity="error" sx={{ mt: 1.5 }}>Turned off by {row.disabled.by || 'the desk'}: {row.disabled.reason || row.disabled.page_class}</Alert>}
                        {row.fixing && <Alert severity="warning" sx={{ mt: 1.5 }}>Being fixed on dev since {(row.fixing.at || '').slice(0, 10)} by {row.fixing.by}: {row.fixing.reason}</Alert>}
                        {row.stop_today && <Alert severity="info" sx={{ mt: 1.5 }}>Retries stopped today by {row.stop_today.by}: {row.stop_today.reason}</Alert>}
                        {row.morning?.note && row.morning.note !== 'clean' && <Alert severity="warning" sx={{ mt: 1.5 }}>06:20 check: {row.morning.note}</Alert>}
                        {drift && <Alert severity="warning" sx={{ mt: 1.5 }}>Schedule: {drift}</Alert>}
                        {life && <Stepper life={life} />}
                    </Box>

                    <Section data-section="jobs">
                        <H>Jobs</H>
                        <Jobs row={row} runs={runs} canSkip={'override_jobs' in row} onSkip={(jt) => act('skip', { jobType: jt })} />
                    </Section>

                    <Section data-section="budget">
                        <H>Sign-ins today</H>
                        <BudgetMeter row={row} office={office} />
                    </Section>

                    <Section data-section="runs">
                        <H>Runs{history ? '' : ' today'}</H>
                        <RunTimeline row={row} runs={runs} shotsByRun={shots} canFix={newer && !row.fixing && !row.disabled}
                            onShot={(s) => act('shot', { shot: s })} onFix={() => act('fix', { deskBusy })}
                            onPromote={(run) => act('promote', { run })} />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button size="small" sx={{ textTransform: 'none', px: 0 }} onClick={() => act('shots')}>All screenshots</Button>
                            <Button size="small" sx={{ textTransform: 'none' }} onClick={() => act('audit')}>Audit log</Button>
                        </Box>
                    </Section>
                </Box>
            )}
        </Drawer>
    );
};

export default LoginDrawer;
